/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppDispatch } from '../../../store/hooks';
import { fetchPermissions } from '../../../features/auth/authSlice';
import { Dialog } from 'sharedUi/Dialog';
import { Dropdown } from 'sharedUi/Dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { Toast } from 'sharedUi/Toast';
import { formatTimestamp } from 'sharedUi/dateUtils';
import { useDebounce } from '../../../hooks/useDebounce';
import { sortRows } from '../../../utils/client-sort';
import SaasDataTable, {
  type SaasColumn,
  buildDefaultFilters,
  buildColumnFiltersParam,
} from '../../../components/common/saas-data-table';
import {
  roleBindingsApi,
  rolesApi,
  permissionSetsApi,
  permissionsApi,
} from '../../../services/rbac.service';
import { unitsService } from '../../../services/units.service';
import { useMinimalUnits } from '../../../hooks/use-units';
import { districtsService } from '../../../services/districts.service';
import { projectsService, type AccessibleProject } from '../../../services/projects.service';
import type {
  RoleBinding,
  RoleBindingCreateRequest,
  RoleBindingUpdateRequest,
  RbacStatus,
  PermissionSet,
  Role,
  AssignmentDependencyRef,
} from '../../../types/rbac';
import type { Unit } from '../../../types';
import SelectionPanel from '../../../components/rbac/selection-panel';
import DependencyDeleteDialog from '../../../components/rbac/dependency-delete-dialog';
import {
  StatusTag,
  useRbacToast,
  Field,
  TextAreaField,
  DetailShell,
  DetailCard,
  DetailRow,
  Chips,
  FormShell,
} from './shared';
import { Button } from 'sharedUi/Button';
import SearchInput from '../../../components/common/search-input';
import { useHasPermission } from '../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../constants/permissions';

interface FormState {
  roleId: string;
  unitId: string;
  description: string;
  inheritChildren: boolean;
  status: RbacStatus;
  localPermissionSetIdsAdd: string[];
  localPermissionKeysRemove: string[];
}

const EMPTY: FormState = {
  roleId: '',
  unitId: '',
  description: '',
  inheritChildren: false,
  status: 'ACTIVE',
  localPermissionSetIdsAdd: [],
  localPermissionKeysRemove: [],
};

// Filterable fields — used to seed the default filter state. Bindings store
// roleId/unitId as ObjectIds, so description is the only text column.
const FILTER_DEFS: SaasColumn[] = [{ field: 'description', header: 'Description', filter: true }];

// ---------------------------------------------------------------------------
// Small presentational helpers (replicate the reference chips / count badge)
// ---------------------------------------------------------------------------
const ChipList = ({
  items,
  max = 3,
  tone = 'slate',
}: {
  items: string[];
  max?: number;
  tone?: 'slate' | 'emerald' | 'rose';
}) => {
  if (!items.length) return <span className="text-gray-400">—</span>;
  const visible = items.slice(0, max);
  const overflow = items.length - max;
  const toneClass =
    tone === 'emerald'
      ? 'bg-emerald-50 text-emerald-700 ring-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-300 dark:ring-emerald-900/40'
      : tone === 'rose'
        ? 'bg-rose-50 text-rose-700 ring-rose-100 dark:bg-rose-900/20 dark:text-rose-300 dark:ring-rose-900/40'
        : 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-gray-700 dark:text-gray-200 dark:ring-gray-600';
  return (
    <div className="flex flex-wrap gap-1">
      {visible.map((item) => (
        <span
          key={item}
          className={`inline-flex items-center rounded-md px-2 py-0.5 font-mono text-xs ring-1 ring-inset ${toneClass}`}
        >
          {item}
        </span>
      ))}
      {overflow > 0 && (
        <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-200 dark:bg-gray-700 dark:text-gray-200 dark:ring-gray-600">
          +{overflow} more
        </span>
      )}
    </div>
  );
};

const PermCountBadge = ({ count }: { count: number }) => {
  if (count === 0) return <span className="text-gray-400">—</span>;
  return (
    <span className="inline-flex items-center rounded-md bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-200 dark:bg-gray-700 dark:text-gray-200 dark:ring-gray-600">
      {count} permission{count !== 1 ? 's' : ''}
    </span>
  );
};

const RoleBindingsTab = () => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const { toast, showSuccess, showError } = useRbacToast();
  // Gate the write controls on the caller's role-binding permissions.
  const canCreate = useHasPermission(PERMISSIONS.AUTH_ROLE_BINDING_CREATE);
  const canUpdate = useHasPermission(PERMISSIONS.AUTH_ROLE_BINDING_UPDATE);
  const canDelete = useHasPermission(PERMISSIONS.AUTH_ROLE_BINDING_DELETE);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  // In-page (client-side) sort of the currently-loaded page. Search + filters stay server-side.
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>(() => buildDefaultFilters(FILTER_DEFS));
  const [viewMode, setViewMode] = useState<'table' | 'tree'>('table');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [showGlobal, setShowGlobal] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  // Dialog-only project scope: drives which Roles/Units/Sets are shown. The binding's
  // real appId is still derived server-side from the chosen unit. null = Global.
  const [formProjectId, setFormProjectId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [viewRow, setViewRow] = useState<RoleBinding | null>(null);
  const [linksRow, setLinksRow] = useState<RoleBinding | null>(null);

  const page = Math.floor(first / pageSize) + 1;
  const filtersParam = buildColumnFiltersParam(filters);
  const appIdParam = selectedProjectId || undefined;

  // Projects the caller may access — drives the project filter (shown when >1).
  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: projectsService.getAccessible,
    staleTime: 5 * 60 * 1000,
  });

  // The dialog project's RBAC policy decides whether a unit is mandatory. A unit-less
  // project (unitModel === 'none') lets us create a global (unit-less) binding.
  const { data: formProjectSettings } = useQuery({
    queryKey: ['project', 'settings', formProjectId],
    queryFn: () => projectsService.getSettings(formProjectId!),
    enabled: !!formProjectId && dialogOpen,
    staleTime: 5 * 60 * 1000,
  });
  // Require a unit only for a specific project that uses units. Global project scope
  // (formProjectId === null) is inherently unit-less. Default to required until the
  // project's settings load, so a unit-using project never briefly looks optional.
  const unitRequired =
    !!formProjectId && (formProjectSettings?.rbac?.unitModel ?? 'required') !== 'none';

  const { data, isFetching } = useQuery({
    queryKey: ['rbac', 'role-bindings', debouncedSearch, page, pageSize, filtersParam, appIdParam, showGlobal],
    queryFn: () =>
      roleBindingsApi.list({
        search: debouncedSearch || undefined,
        page,
        page_size: pageSize,
        filters: filtersParam,
        app_id: appIdParam,
        include_global: showGlobal,
      }),
  });

  const handleFilter = (event: any) => {
    setFilters(event.filters || {});
    setFirst(0);
  };

  // Full list is needed for the tree view (also project-scoped).
  const { data: allBindings = [] } = useQuery({
    queryKey: ['rbac', 'role-bindings', 'all', appIdParam, showGlobal],
    queryFn: () => roleBindingsApi.list({ page: 1, page_size: 1000, app_id: appIdParam, include_global: showGlobal }).then((r) => r.data || []),
  });

  const { data: allRoles = [] } = useQuery({
    queryKey: ['rbac', 'roles', 'all', 'with-global'],
    queryFn: () => rolesApi.listAll(undefined, { includeGlobal: true }),
  });
  const { data: allUnits = [] } = useQuery({
    queryKey: ['rbac', 'units', 'all'],
    queryFn: () => unitsService.getAllForDropdown(),
  });
  // Uncapped, lightweight, cached list of EVERY unit for the picker dropdown
  // (shared hook = fetched once and reused by every unit picker in the app).
  const { data: unitPickList = [] } = useMinimalUnits();
  // include_global so GLOBAL permission sets resolve too — otherwise a binding whose
  // extra set is global can't be mapped to its key and the column shows a raw ObjectId.
  const { data: allSets = [] } = useQuery({
    queryKey: ['rbac', 'permission-sets', 'all', 'with-global'],
    queryFn: () => permissionSetsApi.listAll(undefined, { includeGlobal: true }),
  });
  const { data: allPermissions = [] } = useQuery({
    queryKey: ['rbac', 'permissions', 'all'],
    queryFn: () => permissionsApi.listAll(),
  });
  const { data: allDistricts = [] } = useQuery({
    queryKey: ['districts', 'all', 'dropdown'],
    queryFn: () => districtsService.getAllForDropdown(),
  });
  // Upstream "used by" for the Links dialog (user assignments referencing this binding).
  const { data: linkDeps } = useQuery({
    queryKey: ['rbac', 'dependencies', 'role-binding', linksRow?._id],
    queryFn: () => roleBindingsApi.getDependencies(linksRow!._id),
    enabled: !!linksRow,
    staleTime: 0,
  });

  const roleById = useMemo(() => {
    const m = new Map<string, Role>();
    allRoles.forEach((r) => m.set(r._id, r));
    return m;
  }, [allRoles]);
  const unitById = useMemo(() => {
    const m = new Map<string, Unit>();
    allUnits.forEach((u) => m.set(u._id, u));
    return m;
  }, [allUnits]);
  // Sets keyed by _id AND by key (localAdd holds set _ids, roles hold set keys).
  const setById = useMemo(() => {
    const m = new Map<string, PermissionSet>();
    allSets.forEach((s) => m.set(s._id, s));
    return m;
  }, [allSets]);
  const setByKey = useMemo(() => {
    const m = new Map<string, PermissionSet>();
    allSets.forEach((s) => m.set(s.key, s));
    return m;
  }, [allSets]);
  const districtById = useMemo(() => {
    const m = new Map<string, string>();
    allDistricts.forEach((d) => m.set(d._id, d.name));
    return m;
  }, [allDistricts]);

  // Project NAME lookup + label helper (project name only, no appCode). null = Global.
  const projectNameById = useMemo(() => {
    const m = new Map<string, string>();
    accessibleProjects.forEach((p: AccessibleProject) => m.set(p._id, p.name));
    return m;
  }, [accessibleProjects]);
  const projLabel = useCallback(
    (appId?: string | null) => (appId ? projectNameById.get(appId) || 'Project' : 'Global'),
    [projectNameById]
  );

  const projectOptions = useMemo(
    () => [
      { label: 'Global (all projects)', value: null as string | null },
      ...accessibleProjects.map((p: AccessibleProject) => ({ label: p.name, value: p._id as string | null })),
    ],
    [accessibleProjects]
  );

  // Scope by the dialog's selected project (formProjectId). Matches the backend
  // FK rules: Role/Sets = same-project-or-global; Unit = EXACTLY this project
  // (the unit is what stamps the binding's appId, so it must equal the project).
  const roleAllowed = useCallback(
    (appId?: string | null) => !appId || appId === formProjectId,
    [formProjectId]
  );
  const unitAllowed = useCallback(
    (appId?: string | null) => (appId ?? null) === formProjectId,
    [formProjectId]
  );

  const roleOptions = useMemo(
    () =>
      allRoles
        .filter((r) => roleAllowed(r.appId))
        .map((r) => ({ label: `${r.displayName || r.key} · ${projLabel(r.appId)}`, value: r._id })),
    [allRoles, roleAllowed, projLabel]
  );
  const unitOptions = useMemo(() => {
    const opts = unitPickList
      .filter((u) => unitAllowed((u as any).appId))
      .slice()
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map((u) => ({ label: `${u.name} · ${projLabel((u as any).appId)}`, value: u._id as string }));
    // Unit-less projects (or Global scope) may create a global binding with no unit.
    if (!unitRequired) {
      return [{ label: 'No unit (global)', value: '' }, ...opts];
    }
    return opts;
  }, [unitPickList, unitAllowed, projLabel, unitRequired]);
  const setOptions = useMemo(
    () =>
      allSets
        .filter((s) => roleAllowed(s.appId))
        .map((s) => ({ id: s._id, label: s.displayName || s.key, meta: `${s.key} · ${projLabel(s.appId)}` })),
    [allSets, roleAllowed, projLabel]
  );
  // Remove panel stores permission KEYS (not ids), so option id === permission key.
  const permKeyOptions = useMemo(
    () => allPermissions.map((p) => ({ id: p.key, label: p.displayName || p.key, meta: p.key })),
    [allPermissions]
  );

  const rows = data?.data || [];
  const displayRows = useMemo(() => sortRows(rows, sortField, sortOrder), [rows, sortField, sortOrder]);
  const total = data?.pagination?.totalItems ?? (data?.pagination as any)?.total ?? 0;

  // -------------------------------------------------------------------------
  // Resolution helpers
  // -------------------------------------------------------------------------
  // Role display key/name.
  const roleLabel = (r: RoleBinding) =>
    r.role?.displayName || r.role?.key || roleById.get(r.roleId)?.displayName ||
    roleById.get(r.roleId)?.key || r.roleId;

  // Unit name + district sub-label.
  const unitName = (r: RoleBinding) =>
    r.unit?.name || (r.unitId ? unitById.get(r.unitId)?.name || r.unitId : null);
  const unitSubLabel = (r: RoleBinding): string => {
    if (!r.unitId) return 'Global';
    const unit = unitById.get(r.unitId);
    if (unit?.districtId && districtById.get(unit.districtId)) {
      return districtById.get(unit.districtId)!;
    }
    const path = unit?.parentUnitPath;
    if (path && path.length) return path[path.length - 1];
    return 'Global';
  };

  // localAdd holds permission-set _ids → resolve to their keys for chips.
  const extraSetKeys = (r: RoleBinding): string[] =>
    (r.localAdd || []).map((id) => setById.get(id)?.key || id);

  // localRemove holds permission KEYS directly.
  const blockedKeys = (r: RoleBinding): string[] => r.localRemove || [];

  // Resolved permissions: role default sets (KEYS) expanded to permission keys,
  // union the localAdd sets' permission keys, minus the localRemove keys.
  const resolvedPermissionKeys = (r: RoleBinding): string[] => {
    const keys = new Set<string>();
    const role = roleById.get(r.roleId);
    (role?.defaultPermissionSets || []).forEach((setKey) => {
      setByKey.get(setKey)?.permissionKeys.forEach((pk) => keys.add(pk));
    });
    (r.localAdd || []).forEach((setId) => {
      setById.get(setId)?.permissionKeys.forEach((pk) => keys.add(pk));
    });
    (r.localRemove || []).forEach((pk) => keys.delete(pk));
    return [...keys];
  };

  // Live resolved permissions for the create/edit form: role defaults + localAdd − localRemove.
  const formResolvedKeys = useMemo(() => {
    const keys = new Set<string>();
    const role = roleById.get(form.roleId);
    (role?.defaultPermissionSets || []).forEach((setKey) => {
      setByKey.get(setKey)?.permissionKeys.forEach((pk) => keys.add(pk));
    });
    form.localPermissionSetIdsAdd.forEach((setId) => {
      setById.get(setId)?.permissionKeys.forEach((pk) => keys.add(pk));
    });
    form.localPermissionKeysRemove.forEach((pk) => keys.delete(pk));
    return Array.from(keys).sort();
  }, [
    form.roleId,
    form.localPermissionSetIdsAdd,
    form.localPermissionKeysRemove,
    roleById,
    setByKey,
    setById,
  ]);

  const setField = (k: keyof FormState, v: any) => setForm((p) => ({ ...p, [k]: v }));

  // When the dialog's project changes, drop a Role / Unit / added permission-set
  // that no longer belongs to it — they'd fail the backend's scope checks.
  useEffect(() => {
    if (form.roleId) {
      const r = roleById.get(form.roleId);
      if (r && !roleAllowed(r.appId)) setField('roleId', '');
    }
    if (form.unitId) {
      const u = unitPickList.find((x) => x._id === form.unitId) as any;
      if (u && !unitAllowed(u.appId)) setField('unitId', '');
    }
    const kept = form.localPermissionSetIdsAdd.filter((id) => roleAllowed(setById.get(id)?.appId));
    if (kept.length !== form.localPermissionSetIdsAdd.length) {
      setField('localPermissionSetIdsAdd', kept);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formProjectId]);

  const openCreate = () => {
    setEditId(null);
    setForm(EMPTY);
    // Seed the dialog's project from the list filter if one is active, else Global.
    setFormProjectId(selectedProjectId ?? null);
    setErrors({});
    setDialogOpen(true);
  };

  const openEdit = (row: RoleBinding) => {
    setEditId(row._id);
    setForm({
      roleId: row.roleId || '',
      unitId: row.unitId || '',
      description: row.description || '',
      inheritChildren: !!row.inheritChildren,
      status: row.status || 'ACTIVE',
      localPermissionSetIdsAdd: row.localAdd || [],
      localPermissionKeysRemove: row.localRemove || [],
    });
    // The binding's project is stored on the row (derived from its unit).
    setFormProjectId((row.appId as string) ?? null);
    setErrors({});
    setDialogOpen(true);
  };

  // Selecting a Role aligns the dialog's project to the role's own project (a
  // project-specific role can only bind within its project). A global role leaves
  // the project as-is (it may bind in any project).
  const onRoleChange = (roleId: string) => {
    setField('roleId', roleId);
    const r = roleById.get(roleId);
    if (r?.appId && r.appId !== formProjectId) setFormProjectId(r.appId);
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['rbac', 'role-bindings'] });
    // Binding changes can alter the current user's effective permissions.
    dispatch(fetchPermissions());
  };

  const createMut = useMutation({
    mutationFn: (payload: RoleBindingCreateRequest) => roleBindingsApi.create(payload),
    onSuccess: () => {
      invalidate();
      showSuccess('Role binding created');
      setDialogOpen(false);
    },
    onError: (e) => showError(e, 'Failed to create role binding'),
  });

  const updateMut = useMutation({
    mutationFn: (payload: RoleBindingUpdateRequest) => roleBindingsApi.update(editId!, payload),
    onSuccess: () => {
      invalidate();
      showSuccess('Role binding updated');
      setDialogOpen(false);
    },
    onError: (e) => showError(e, 'Failed to update role binding'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => roleBindingsApi.remove(id),
    onSuccess: () => {
      invalidate();
      showSuccess('Role binding deleted');
      setDeleteId(null);
    },
    onError: (e) => {
      showError(e, 'Failed to delete role binding');
      setDeleteId(null);
    },
  });

  const saving = createMut.isPending || updateMut.isPending;

  const validate = () => {
    const err: Record<string, string> = {};
    if (!form.roleId) err.roleId = 'Role is required';
    if (unitRequired && !form.unitId) err.unitId = 'Unit is required';
    // Project consistency (mirrors the backend same-project-or-global FK rule): the
    // binding's project is the unit's project; the Role and every added set must be
    // that project or global.
    if (form.roleId && form.unitId) {
      const unit = unitPickList.find((x) => x._id === form.unitId) as any;
      const unitApp = (unit?.appId as string) ?? null;
      const okScope = (appId?: string | null) => !appId || appId === unitApp;
      const r = roleById.get(form.roleId);
      if (r && !okScope(r.appId)) {
        err.roleId = `Role must belong to the unit's project (${projLabel(unitApp)}) or be global.`;
      }
      const badSet = form.localPermissionSetIdsAdd.find((id) => !okScope(setById.get(id)?.appId));
      if (badSet) {
        err.roleId = err.roleId || `Added permission sets must belong to ${projLabel(unitApp)} or be global.`;
      }
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const save = () => {
    if (!validate()) return;
    const payload: RoleBindingCreateRequest = {
      roleId: form.roleId,
      // Empty unit → a global (unit-less) binding (unit-less projects / global scope).
      unitId: form.unitId || undefined,
      description: form.description || undefined,
      inheritChildren: form.inheritChildren,
      localPermissionSetIdsAdd: form.localPermissionSetIdsAdd,
      localPermissionKeysRemove: form.localPermissionKeysRemove,
      status: form.status,
    };
    if (editId) updateMut.mutate(payload);
    else createMut.mutate(payload);
  };

  // -------------------------------------------------------------------------
  // Actions cell (shared by table + tree)
  // -------------------------------------------------------------------------
  const ActionButtons = ({ r, showDelete = true }: { r: RoleBinding; showDelete?: boolean }) => (
    <div className="flex items-center gap-1">
      <button
        className="rounded p-2 transition-colors hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
        onClick={() => setViewRow(r)}
        aria-label="View"
        title="View"
      >
        <i className="pi pi-eye text-emerald-600" />
      </button>
      <button
        className="rounded p-2 transition-colors hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
        onClick={() => setLinksRow(r)}
        aria-label="Links"
        title="Links"
      >
        <i className="pi pi-sitemap text-indigo-600" />
      </button>
      {canUpdate && (
        <button
          className="rounded p-2 transition-colors hover:bg-blue-50 dark:hover:bg-blue-900/20"
          onClick={() => openEdit(r)}
          aria-label="Edit"
          title="Edit"
        >
          <i className="pi pi-pencil text-blue-600" />
        </button>
      )}
      {showDelete && canDelete && (
        <button
          className="rounded p-2 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20"
          onClick={() => setDeleteId(r._id)}
          aria-label="Delete"
          title="Delete"
        >
          <i className="pi pi-trash text-red-600" />
        </button>
      )}
    </div>
  );

  const columns: SaasColumn<RoleBinding>[] = useMemo(
    () => [
      {
        field: 'unit.name',
        header: 'UNIT',
        sortable: true,
        hideable: false,
        // Sort by the populated unit name (roleId/unitId are opaque ObjectIds).
        body: (r) => (
          <div className="leading-tight">
            <div className="font-semibold text-ink">
              {unitName(r) || <span className="italic font-normal text-gray-400">Global</span>}
            </div>
            <span className="mt-0.5 inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 dark:bg-gray-700 dark:text-gray-300">
              {unitSubLabel(r)}
            </span>
          </div>
        ),
      },
      {
        field: 'role.key',
        header: 'ROLE',
        sortable: true,
        // Sort by the populated role key (roleId is an opaque ObjectId).
        body: (r) => (
          <div className="leading-tight">
            <div className="font-mono text-xs text-ink">{roleLabel(r)}</div>
            <div className="mt-1">
              <StatusTag status={r.status} />
            </div>
          </div>
        ),
      },
      {
        field: 'extra',
        header: 'EXTRA PERMISSIONS',
        body: (r) => <ChipList items={extraSetKeys(r)} tone="slate" />,
      },
      {
        field: 'blocked',
        header: 'BLOCKED PERMISSIONS',
        body: (r) => <ChipList items={blockedKeys(r)} tone="slate" />,
      },
      {
        field: 'resolved',
        header: 'RESOLVED PERMISSIONS',
        body: (r) => <PermCountBadge count={resolvedPermissionKeys(r).length} />,
      },
      {
        field: 'actions',
        header: 'ACTIONS',
        align: 'center',
        hideable: false,
        body: (r) => (
          <div className="flex justify-center">
            <ActionButtons r={r} />
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roleById, unitById, setById, setByKey, districtById, accessibleProjects]
  );

  // -------------------------------------------------------------------------
  // Tree view: build unit hierarchy from parentUnitId, nest bindings.
  // -------------------------------------------------------------------------
  const bindingsByUnit = useMemo(() => {
    const m = new Map<string, RoleBinding[]>();
    allBindings.forEach((b) => {
      const key = b.unitId || '__GLOBAL__';
      const list = m.get(key) || [];
      list.push(b);
      m.set(key, list);
    });
    return m;
  }, [allBindings]);

  const unitTree = useMemo(() => {
    const nodeById = new Map<string, UnitTreeNode>();
    allUnits.forEach((u) => nodeById.set(u._id, { unit: u, children: [] }));
    const roots: UnitTreeNode[] = [];
    allUnits.forEach((u) => {
      const node = nodeById.get(u._id)!;
      const pid = u.parentUnitId ? String(u.parentUnitId) : null;
      const parent = pid ? nodeById.get(pid) : undefined;
      if (parent) parent.children.push(node);
      else roots.push(node);
    });
    return roots;
  }, [allUnits]);

  const globalBindings = bindingsByUnit.get('__GLOBAL__') || [];

  // Full-page detail view (replaces the old dialog). Reuses the tab's already
  // loaded lookups + resolution helpers, so it needs no extra fetch.
  if (viewRow) {
    const roleSets = roleById.get(viewRow.roleId)?.defaultPermissionSets || [];
    const extras = extraSetKeys(viewRow);
    const blocked = blockedKeys(viewRow);
    const resolved = resolvedPermissionKeys(viewRow);
    const createdAt = (viewRow as any).createdAt;
    const updatedAt = (viewRow as any).updatedAt;
    return (
      <>
        <Toast ref={toast} position="top-right" />
        <DetailShell
          backLabel="Back to Role Bindings"
          onBack={() => setViewRow(null)}
          title={roleLabel(viewRow)}
          subtitle={`${unitName(viewRow) || 'Global'} · ${projLabel(viewRow.appId)}`}
          actions={
            <>
              <Button label="Links" icon="pi pi-sitemap" severity="secondary" outlined size="small" onClick={() => setLinksRow(viewRow)} />
              {canUpdate && <Button label="Edit" icon="pi pi-pencil" size="small" onClick={() => { const r = viewRow; setViewRow(null); openEdit(r); }} />}
            </>
          }
        >
          <div className="grid gap-3 md:grid-cols-2">
            <DetailCard title="Binding">
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                <DetailRow label="Role" value={roleLabel(viewRow)} mono />
                <DetailRow label="Status" value={<StatusTag status={viewRow.status} />} />
                <DetailRow label="Unit" value={unitName(viewRow) || 'Global'} />
                <DetailRow label="District / Scope" value={unitSubLabel(viewRow)} />
                <DetailRow label="Project" value={projLabel(viewRow.appId)} />
                <DetailRow label="Inherit children" value={viewRow.inheritChildren ? 'Yes' : 'No'} />
                {viewRow.description && (
                  <div className="col-span-2">
                    <DetailRow label="Description" value={viewRow.description} />
                  </div>
                )}
              </div>
            </DetailCard>

            <DetailCard title="Audit">
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                {createdAt && <DetailRow label="Created" value={formatTimestamp(createdAt)} />}
                {updatedAt && <DetailRow label="Updated" value={formatTimestamp(updatedAt)} />}
                <div className="col-span-2">
                  <DetailRow label="Binding ID" value={viewRow._id} mono />
                </div>
              </div>
            </DetailCard>
          </div>

          <div className="mt-3 grid gap-3">
            <DetailCard title="Role default permission sets" count={roleSets.length}>
              <Chips items={roleSets} tone="slate" />
            </DetailCard>
            <div className="grid gap-3 md:grid-cols-2">
              <DetailCard title="Extra permission sets" count={extras.length}>
                <Chips items={extras} tone="emerald" />
              </DetailCard>
              <DetailCard title="Blocked permissions" count={blocked.length}>
                <Chips items={blocked} tone="rose" />
              </DetailCard>
            </div>
            <DetailCard title="Resolved permissions" count={resolved.length}>
              <div className="max-h-72 overflow-auto">
                <Chips items={resolved} tone="slate" />
              </div>
            </DetailCard>
          </div>
        </DetailShell>

        {/* Links dialog still available from the detail page */}
        <Dialog
          header="Role Binding Links"
          visible={!!linksRow}
          onHide={() => setLinksRow(null)}
          style={{ width: '720px', maxWidth: '95vw' }}
          modal
        >
          {linksRow && (
            <LinksContent
              binding={linksRow}
              roleLabel={roleLabel(linksRow)}
              roleSets={roleById.get(linksRow.roleId)?.defaultPermissionSets || []}
              unitName={unitName(linksRow)}
              unitDistrict={unitSubLabel(linksRow)}
              extraSets={extraSetKeys(linksRow)}
              blocked={blockedKeys(linksRow)}
              resolved={resolvedPermissionKeys(linksRow)}
              usedByAssignments={linkDeps?.dependents.assignments || []}
            />
          )}
        </Dialog>
      </>
    );
  }

  // Full-page create / edit form (replaces the old dialog).
  if (dialogOpen) {
    return (
      <>
        <Toast ref={toast} position="top-right" />
        <FormShell
          backLabel="Back to Role Bindings"
          onBack={() => !saving && setDialogOpen(false)}
          title={editId ? 'Edit Role Binding' : 'Create Role Binding'}
          subtitle="Bind a role to a unit and optionally allow or block specific permissions locally."
          onCancel={() => setDialogOpen(false)}
          onSave={save}
          saving={saving}
          isEdit={!!editId}
        >
          <div className="flex flex-col gap-3">
            {/* Project scope: filters the Role & Unit lists. The binding's real appId
                is derived from the chosen unit on the backend. Immutable on edit. */}
            <Field label="Project">
              <Dropdown
                value={formProjectId}
                options={projectOptions}
                onChange={(e) => setFormProjectId(e.value ?? null)}
                placeholder="Select project"
                filter
                disabled={!!editId}
                className="w-full md:w-1/2"
              />
            </Field>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Role" required error={errors.roleId}>
                <Dropdown
                  value={form.roleId}
                  options={roleOptions}
                  onChange={(e) => onRoleChange(e.value)}
                  placeholder="Select role"
                  filter
                  className="w-full"
                />
              </Field>
              <Field label="Unit" required={unitRequired} error={errors.unitId}>
                <Dropdown
                  value={form.unitId}
                  options={unitOptions}
                  onChange={(e) => setField('unitId', e.value)}
                  placeholder={unitRequired ? 'Select unit' : 'Select unit (optional)'}
                  filter
                  resetFilterOnHide
                  virtualScrollerOptions={{ itemSize: 38 }}
                  className="w-full"
                />
              </Field>
            </div>

            <Field label="Description">
              <TextAreaField
                value={form.description}
                onChange={(v) => setField('description', v)}
                placeholder="Optional description"
                rows={2}
              />
            </Field>

            <div className="flex items-center gap-2">
              <InputSwitch
                checked={form.inheritChildren}
                onChange={(e) => setField('inheritChildren', e.value)}
              />
              <label className="text-sm text-gray-700 dark:text-gray-300">Inherit children</label>
            </div>

            <SelectionPanel
              title="Add permission sets"
              helper={`${form.localPermissionSetIdsAdd.length} selected`}
              options={setOptions}
              selectedIds={form.localPermissionSetIdsAdd}
              onChange={(ids) => setField('localPermissionSetIdsAdd', ids)}
            />

            <SelectionPanel
              title="Remove permissions"
              helper={`${form.localPermissionKeysRemove.length} selected`}
              options={permKeyOptions}
              selectedIds={form.localPermissionKeysRemove}
              onChange={(keys) => setField('localPermissionKeysRemove', keys)}
            />

            {/* Resolved permissions preview (read-only): role defaults + added − removed. */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Resolved permissions ({formResolvedKeys.length})
              </label>
              <div className="max-h-32 overflow-auto rounded-md border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-900/40">
                {formResolvedKeys.length === 0 ? (
                  <span className="text-xs text-ink-muted">
                    Pick a role (and optionally add/remove sets) to preview the effective permissions.
                  </span>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {formResolvedKeys.map((k) => (
                      <span
                        key={k}
                        className="rounded bg-gray-200 px-2 py-0.5 font-mono text-[11px] text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                      >
                        {k}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

          </div>
        </FormShell>
      </>
    );
  }

  return (
    <div>
      <Toast ref={toast} position="top-right" />

      {/* Header + toolbar */}
      <div className="mb-3">
        <p className="mb-3 text-sm text-ink-muted">
          Bind a role to a unit and optionally allow or block specific permissions locally.
        </p>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <SearchInput
              value={search}
              onChange={(v) => { setSearch(v); setFirst(0); }}
              placeholder="Search role bindings..."
              width={240}
            />
            {/* Project filter (bindings are scoped by their unit's project) */}
            {accessibleProjects.length > 1 && (
              <Dropdown
                value={selectedProjectId}
                options={[
                  { label: 'All projects', value: null },
                  ...accessibleProjects.map((p: AccessibleProject) => ({
                    label: p.name,
                    value: p._id,
                  })),
                ]}
                onChange={(e: any) => { setSelectedProjectId(e.value ?? null); setFirst(0); }}
                placeholder="All projects"
                className="w-56"
              />
            )}
            {/* View toggle: Table | Tree */}
            <div className="inline-flex overflow-hidden rounded-md border border-gray-300 dark:border-gray-600">
              <button
                onClick={() => setViewMode('table')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${
                  viewMode === 'table'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-canvas dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800'
                }`}
              >
                <i className="pi pi-table text-xs" />
                Table
              </button>
              <button
                onClick={() => setViewMode('tree')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${
                  viewMode === 'tree'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-canvas dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800'
                }`}
              >
                <i className="pi pi-sitemap text-xs" />
                Tree
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
              <InputSwitch checked={showGlobal} onChange={(e: any) => { setShowGlobal(!!e.value); setFirst(0); }} />
              Show core
            </label>
            <span className="whitespace-nowrap text-xs text-ink-muted">
              {total} {total === 1 ? 'record' : 'records'}
            </span>
            <button
              onClick={() => queryClient.invalidateQueries({ queryKey: ['rbac', 'role-bindings'] })}
              className="rounded-md border border-gray-300 p-2 text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
              aria-label="Refresh"
              title="Refresh"
            >
              <i className={`pi pi-refresh text-sm ${isFetching ? 'pi-spin' : ''}`} />
            </button>
            {canCreate && <button
              onClick={openCreate}
              className="flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-indigo-700"
            >
              <i className="pi pi-plus text-xs" />
              New Role Binding
            </button>}
          </div>
        </div>
      </div>

      {viewMode === 'table' ? (
        <SaasDataTable
          tableId="rbac-role-bindings"
          columns={columns}
          data={displayRows}
          loading={isFetching}
          dataKey="_id"
          emptyMessage="No role bindings found"
          first={first}
          rows={pageSize}
          totalRecords={total}
          onPage={(e: any) => {
            setFirst(e.first);
            setPageSize(e.rows);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sortField={sortField ?? undefined}
          sortOrder={sortOrder ?? undefined}
          onSort={(e: any) => {
            setSortField(e.sortField);
            setSortOrder(e.sortOrder);
          }}
          filters={filters}
          onFilter={handleFilter}
          testId="RoleBindingsList"
        />
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
          {allUnits.length === 0 && globalBindings.length === 0 ? (
            <p className="p-6 text-center text-sm text-gray-400">No units or role bindings found</p>
          ) : (
            <div className="space-y-1">
              {globalBindings.length > 0 && (
                <GlobalGroup
                  bindings={globalBindings}
                  roleLabel={roleLabel}
                  onView={setViewRow}
                  onLinks={setLinksRow}
                  onEdit={canUpdate ? openEdit : undefined}
                  onDelete={canDelete ? (r) => setDeleteId(r._id) : undefined}
                />
              )}
              {unitTree.map((node) => (
                <UnitNode
                  key={node.unit._id}
                  node={node}
                  depth={0}
                  bindingsByUnit={bindingsByUnit}
                  roleLabel={roleLabel}
                  onView={setViewRow}
                  onLinks={setLinksRow}
                  onEdit={canUpdate ? openEdit : undefined}
                  onDelete={canDelete ? (r) => setDeleteId(r._id) : undefined}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <DependencyDeleteDialog
        entityId={deleteId}
        entityLabel="role binding"
        resourceKey="role-binding"
        getDependencies={roleBindingsApi.getDependencies}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => deleteId && deleteMut.mutate(deleteId)}
        deleting={deleteMut.isPending}
      />

      {/* Links dialog: what this binding grants */}
      <Dialog
        header="Role Binding Links"
        visible={!!linksRow}
        onHide={() => setLinksRow(null)}
        style={{ width: '720px', maxWidth: '95vw' }}
        modal
      >
        {linksRow && (
          <LinksContent
            binding={linksRow}
            roleLabel={roleLabel(linksRow)}
            roleSets={roleById.get(linksRow.roleId)?.defaultPermissionSets || []}
            unitName={unitName(linksRow)}
            unitDistrict={unitSubLabel(linksRow)}
            extraSets={extraSetKeys(linksRow)}
            blocked={blockedKeys(linksRow)}
            resolved={resolvedPermissionKeys(linksRow)}
            usedByAssignments={linkDeps?.dependents.assignments || []}
          />
        )}
      </Dialog>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Links dialog content
// ---------------------------------------------------------------------------
const LinkSection = ({
  icon,
  title,
  count,
  children,
}: {
  icon: string;
  title: string;
  count?: number;
  children: React.ReactNode;
}) => (
  <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
    <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5 dark:border-gray-700">
      <div className="flex items-center gap-2">
        <i className={`${icon} text-indigo-600`} />
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
      </div>
      {count !== undefined && (
        <span className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-100 dark:bg-indigo-900/20 dark:text-indigo-300">
          {count}
        </span>
      )}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const LinksContent = ({
  binding,
  roleLabel,
  roleSets,
  unitName,
  unitDistrict,
  extraSets,
  blocked,
  resolved,
  usedByAssignments,
}: {
  binding: RoleBinding;
  roleLabel: string;
  roleSets: string[];
  unitName: string | null;
  unitDistrict: string;
  extraSets: string[];
  blocked: string[];
  resolved: string[];
  usedByAssignments: AssignmentDependencyRef[];
}) => (
  <div className="flex flex-col gap-3 pt-1">
    <div className="grid gap-3 md:grid-cols-2">
      <LinkSection icon="pi pi-id-card" title="Role">
        <div className="font-mono text-sm text-ink">{roleLabel}</div>
        {roleSets.length > 0 ? (
          <div className="mt-2">
            <ChipList items={roleSets} max={20} tone="slate" />
          </div>
        ) : (
          <p className="mt-1 text-xs text-gray-400">No default permission sets.</p>
        )}
      </LinkSection>

      <LinkSection icon="pi pi-building" title="Unit">
        <div className="text-sm font-medium text-ink">
          {unitName || 'Global'}
        </div>
        <div className="mt-1 text-xs text-ink-muted">{unitDistrict}</div>
        <div className="mt-2 text-xs text-ink-muted">
          Inherit to children:{' '}
          <span className="font-medium text-gray-700 dark:text-gray-200">
            {binding.inheritChildren ? 'Yes' : 'No'}
          </span>
        </div>
      </LinkSection>
    </div>

    <LinkSection icon="pi pi-plus-circle" title="Extra Permission Sets" count={extraSets.length}>
      <ChipList items={extraSets} max={50} tone="emerald" />
    </LinkSection>

    <LinkSection icon="pi pi-minus-circle" title="Blocked Permissions" count={blocked.length}>
      <ChipList items={blocked} max={50} tone="rose" />
    </LinkSection>

    <LinkSection icon="pi pi-shield" title="Resolved Permissions" count={resolved.length}>
      <ChipList items={resolved} max={200} tone="slate" />
    </LinkSection>

    <LinkSection icon="pi pi-id-card" title="Used by User Assignments" count={usedByAssignments.length}>
      {usedByAssignments.length ? (
        <ChipList items={usedByAssignments.map((a) => a.user || a._id)} max={50} tone="slate" />
      ) : (
        <p className="text-xs text-gray-400">No user assignments use this binding.</p>
      )}
    </LinkSection>
  </div>
);

// ---------------------------------------------------------------------------
// Tree view components
// ---------------------------------------------------------------------------
interface UnitTreeNode {
  unit: Unit;
  children: UnitTreeNode[];
}

const BindingRow = ({
  binding,
  roleLabel,
  onView,
  onLinks,
  onEdit,
  onDelete,
}: {
  binding: RoleBinding;
  roleLabel: (r: RoleBinding) => string;
  onView: (r: RoleBinding) => void;
  onLinks: (r: RoleBinding) => void;
  onEdit?: (r: RoleBinding) => void;
  onDelete?: (r: RoleBinding) => void;
}) => {
  const isActive = (binding.status || 'ACTIVE') === 'ACTIVE';
  return (
    <div
      className={`flex items-center gap-3 rounded-xl border border-l-[3px] bg-white px-4 py-2.5 shadow-sm dark:bg-gray-800 ${
        isActive
          ? 'border-gray-200 border-l-emerald-400 dark:border-gray-700'
          : 'border-gray-200 border-l-amber-400 dark:border-gray-700'
      }`}
    >
      <div
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
          isActive ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-amber-50 dark:bg-amber-900/20'
        }`}
      >
        <i className={`pi pi-shield ${isActive ? 'text-emerald-600' : 'text-amber-600'}`} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-mono text-sm font-medium text-gray-800 dark:text-gray-100">
          {roleLabel(binding)}
        </div>
        {binding.description && (
          <div className="truncate text-xs italic text-gray-400" title={binding.description}>
            {binding.description}
          </div>
        )}
      </div>
      <StatusTag status={binding.status} />
      <div className="flex shrink-0 items-center gap-1">
        <button aria-label="View"
          className="rounded p-1.5 transition-colors hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
          onClick={() => onView(binding)}
          title="View"
        >
          <i className="pi pi-eye text-emerald-600" />
        </button>
        <button aria-label="Links"
          className="rounded p-1.5 transition-colors hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
          onClick={() => onLinks(binding)}
          title="Links"
        >
          <i className="pi pi-sitemap text-indigo-600" />
        </button>
        {onEdit && (
          <button aria-label="Edit"
            className="rounded p-1.5 transition-colors hover:bg-blue-50 dark:hover:bg-blue-900/20"
            onClick={() => onEdit(binding)}
            title="Edit"
          >
            <i className="pi pi-pencil text-blue-600" />
          </button>
        )}
        {onDelete && (
          <button aria-label="Delete"
            className="rounded p-1.5 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20"
            onClick={() => onDelete(binding)}
            title="Delete"
          >
            <i className="pi pi-trash text-red-600" />
          </button>
        )}
      </div>
    </div>
  );
};

const GlobalGroup = ({
  bindings,
  roleLabel,
  onView,
  onLinks,
  onEdit,
  onDelete,
}: {
  bindings: RoleBinding[];
  roleLabel: (r: RoleBinding) => string;
  onView: (r: RoleBinding) => void;
  onLinks: (r: RoleBinding) => void;
  onEdit?: (r: RoleBinding) => void;
  onDelete?: (r: RoleBinding) => void;
}) => {
  const [expanded, setExpanded] = useState(true);
  return (
    <div className="mb-1">
      <div className="mb-2 flex items-center gap-2.5 rounded-xl border border-indigo-100 bg-gradient-to-r from-indigo-50 to-transparent px-3 py-2.5 dark:border-indigo-900/40 dark:from-indigo-900/20">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex h-6 w-6 items-center justify-center rounded-md text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/40"
        >
          <i className={`pi pi-chevron-right text-sm transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </button>
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-900/40">
          <i className="pi pi-globe text-indigo-600" />
        </div>
        <span className="flex-1 text-sm font-bold text-indigo-900 dark:text-indigo-200">Global</span>
        <span className="rounded-full bg-indigo-100 px-2.5 py-0.5 text-[11px] font-semibold text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
          {bindings.length} binding{bindings.length !== 1 ? 's' : ''}
        </span>
      </div>
      {expanded && (
        <div className="ml-9 mb-3 space-y-2">
          {bindings.map((b) => (
            <BindingRow
              key={b._id}
              binding={b}
              roleLabel={roleLabel}
              onView={onView}
              onLinks={onLinks}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const UnitNode = ({
  node,
  depth,
  bindingsByUnit,
  roleLabel,
  onView,
  onLinks,
  onEdit,
  onDelete,
}: {
  node: UnitTreeNode;
  depth: number;
  bindingsByUnit: Map<string, RoleBinding[]>;
  roleLabel: (r: RoleBinding) => string;
  onView: (r: RoleBinding) => void;
  onLinks: (r: RoleBinding) => void;
  onEdit?: (r: RoleBinding) => void;
  onDelete?: (r: RoleBinding) => void;
}) => {
  const bindings = bindingsByUnit.get(node.unit._id) || [];
  const hasBindings = bindings.length > 0;
  const hasChildren = node.children.length > 0;
  const isExpandable = hasBindings || hasChildren;
  const [expanded, setExpanded] = useState(hasBindings);
  const isRoot = depth === 0;

  return (
    <div className={depth > 0 ? 'ml-5 border-l-2 border-slate-200 pl-4 dark:border-gray-700' : 'mb-1'}>
      <div
        className={`group flex select-none items-center gap-2.5 rounded-xl px-3 transition-all ${
          isRoot
            ? 'mb-2 border border-indigo-100 bg-gradient-to-r from-indigo-50 to-transparent py-2.5 dark:border-indigo-900/40 dark:from-indigo-900/20'
            : 'border border-transparent py-2 hover:border-slate-100 hover:bg-slate-50 dark:hover:bg-gray-700/40'
        }`}
      >
        <button
          onClick={() => setExpanded((v) => !v)}
          disabled={!isExpandable}
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors ${
            isExpandable
              ? 'text-slate-400 hover:bg-slate-200 dark:hover:bg-gray-600'
              : 'pointer-events-none opacity-0'
          }`}
        >
          <i className={`pi pi-chevron-right text-xs transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </button>
        <div
          className={`flex shrink-0 items-center justify-center rounded-lg ${
            isRoot ? 'h-7 w-7 bg-indigo-100 dark:bg-indigo-900/40' : 'h-6 w-6 bg-slate-100 dark:bg-gray-700'
          }`}
        >
          <i
            className={`pi pi-building text-xs ${isRoot ? 'text-indigo-600' : 'text-slate-500 dark:text-gray-300'}`}
          />
        </div>
        <span
          className={`min-w-0 flex-1 truncate text-sm ${
            isRoot
              ? 'font-bold text-indigo-900 dark:text-indigo-200'
              : 'font-semibold text-slate-700 dark:text-gray-200'
          }`}
        >
          {node.unit.name || node.unit.unitCode || node.unit._id}
        </span>
        {node.unit.unitCode && (
          <span
            className={`shrink-0 rounded-md px-2 py-0.5 font-mono text-[11px] font-bold tracking-wide ${
              isRoot
                ? 'bg-indigo-600 text-white'
                : 'bg-slate-100 text-slate-500 ring-1 ring-inset ring-slate-200 dark:bg-gray-700 dark:text-gray-300 dark:ring-gray-600'
            }`}
          >
            {node.unit.unitCode}
          </span>
        )}
        {hasBindings && (
          <span className="shrink-0 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-600 ring-1 ring-inset ring-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-300">
            {bindings.length} binding{bindings.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {expanded && (
        <>
          {hasBindings && (
            <div className="ml-9 mb-3 mt-1 space-y-2">
              {bindings.map((b) => (
                <BindingRow
                  key={b._id}
                  binding={b}
                  roleLabel={roleLabel}
                  onView={onView}
                  onLinks={onLinks}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}
            </div>
          )}
          {hasChildren && (
            <div className="space-y-0.5">
              {node.children.map((child) => (
                <UnitNode
                  key={child.unit._id}
                  node={child}
                  depth={depth + 1}
                  bindingsByUnit={bindingsByUnit}
                  roleLabel={roleLabel}
                  onView={onView}
                  onLinks={onLinks}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RoleBindingsTab;
